<template>
  <div id="create-account-header" class="flex flex-col grow h-screen">
    <div class="w-full flex flex-row relative bg-tertiary justify-between pl-6 xl:px-20 min-h-23">
      <div class="flex flex-row gap-4">
        <logo class="flex self-center" />
        <div v-if="whitelabel.logo" class="border-r flex border-primary w-full" />
        <div class="flex items-center h-full object-contain">
          <img
            v-if="whitelabel.logo"
            :src="whitelabel.logo"
            class="max-w-navBarHeight h-auto"
            alt="otivo-logo" />
        </div>
      </div>

      <div
        class="gap-6 md:flex flex-row self-center"
        v-if="whitelabel.identifier === 'COLONIAL_FIRST_STATE'">
        <a
          href="tel:1300695944"
          class="button-1 text-blue-2 mr-10 xl:mr-0 flex gap-1 hover:underline"
          >Need help?<span class="flex"><PhoneIcon /></span> 1300 695 944</a
        >
      </div>
    </div>

    <div class="flex flex-col bg-blue-6 grow items-center">
      <div class="flex mt-10 md:mt-20">
        <AccountCreationProgress :complete-percentage="progress" />
      </div>
      <div
        class="container flex flex-col px-5 md:px-10 w-full h-full py-5 sm:py-10 md:py-20 items-center">
        <RouterView @next="handleNext"></RouterView>
      </div>
    </div>
    <CustomFooter class="bg-tertiary text-blue-4 relative w-full md:pt-4 z-10" />
  </div>
</template>

<script setup lang="ts">
import AccountCreationProgress from '@/components/CreateAccount/Partials/AccountCreationProgress.vue'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast.ts'
import { computed, onMounted } from 'vue'
import { useSessionStore } from '@/store/pinia/SessionStore.ts'
import CustomFooter from '@/components/Footer/CustomFooter.vue'
import Logo from '@/components/Logo/Logo.vue'
import { isCFSWhitelabel } from '@/composables/isCFSWhitelabel.ts'
import PhoneIcon from '@/components/icons/PhoneIcon.vue'
import { useUserStore } from '@/store/pinia/UserStore'

const { successToast } = useToast()
const router = useRouter()
const sessionStore = useSessionStore()
const whitelabel = sessionStore.getWhitelabelData
const userStore = useUserStore()
const user = computed(() => userStore.getUser)

// handling forward and back navigation for percentage bar
const progress = computed(() => {
  let value = 5
  switch (router.currentRoute.value.name) {
    case 'CFSDisclaimer':
      return 5
    case 'CaptureFundMemberId':
      return 30
    case 'CaptureSuperAccountNumber':
      return 45
    case 'CaptureSuperBalance':
      return 60
    case 'CaptureInvestmentOptions':
      return 75
    case 'SelectSuperFund':
      return 45
    case 'Paywall':
      return 95
    case 'TermsAndConditionsForm':
      return 85
    case 'SuperFundDisclaimer':
      return 85
  }
  return value
})

// Initial routing logic to determine first step based on whitelabel
onMounted(() => {
  // Only redirect if we're on the base create-account route with no specific child route
  if (router.currentRoute.value.name === 'create-account') {
    if (isCFSWhitelabel()) {
      router.replace({ name: 'CFSDisclaimer' })
    } else {
      router.replace({ name: 'UserDetailsForm' })
    }
  }
})

const handleNext = (val: string | number) => {
  switch (val) {
    case 201:
      if (whitelabel.superfund_config?.superfund_account_creation_enabled ?? false)
        router.push({ name: 'CaptureFundMemberId' })
      // ELSE GO TO NEXT REGULAR OTIVO ACCOUNT CREATION STEP
      else router.push({ name: 'SelectSuperFund' })
      break

    case 'member id verified':
      successToast('Member ID verified')
      router.push({ name: 'CaptureSuperAccountNumber' })
      break

    // SUPER FUND ACCOUNT CREATION STEPS
    case 'captured account number':
      router.push({ name: 'CaptureSuperBalance' })
      break

    case 'captured balance':
      router.push({ name: 'CaptureInvestmentOptions' })
      break

    case 'super details captured':
      successToast('Super details captured')

      if (
        !user.value.has_consented &&
        (whitelabel.superfund_config?.superfund_account_creation_enabled ?? false)
      ) {
        router.push({ name: 'SuperFundDisclaimer' })
        break
      }
      router.push({ name: 'TermsAndConditionsForm' })
      break
    // END

    case 'captured super fund':
      if (
        !user.value.has_consented &&
        (whitelabel.superfund_config?.superfund_account_creation_enabled ?? false)
      ) {
        router.push({ name: 'SuperFundDisclaimer' })
        break
      }
      router.push({ name: 'TermsAndConditionsForm' })
      break

    case 'terms and conditions accepted':
      if (whitelabel.superfund_config?.superfund_account_creation_enabled ?? false) {
        goToDashboard()
      } else {
        router.push({ name: 'Paywall' })
      }
      break

    case 'paid':
      goToDashboard()
      break

    default:
      throw new Error('Error handling next step in account creation process')
  }
}

const goToDashboard = () => {
  setTimeout(() => router.push({ name: 'clientDashboard' }), 1000)
}
</script>

<style scoped></style>
